import { Box, List, ListItem, ListItemButton, ListItemText, Divider } from "@mui/material";
import { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useTabs } from "../../../context/TabContext";
import Iconify from "../../../reusable-components/iconify";
import { ScopeGuard, useScopeCheckFn } from "../../../reusable-components/scopes";
import { useHeaderItems } from "../headerItems";

MobileHeader.propTypes = {
  setIsMobileNavOpen: PropTypes.func,
}
export default function MobileHeader({setIsMobileNavOpen}) {

  const {headerItems, checkPath} = useHeaderItems();
  
  const scopeCheck = useScopeCheckFn();

  const itemsAreAllowed = (items = []) => items.some(item => {
    if (!item.scopes?.length) return true;
    return !!(scopeCheck(item.scopes, item.allowAdmin || false));
  });


  const [subnav, setSubnav] = useState(
    headerItems.find(item => itemsAreAllowed(item?.subitems) && item.subitems?.find(subitem => checkPath(subitem.path)))?.label || null
  );

  const { clearFiltersAndNavigate } = useTabs();

  const handleClick = (item, closeNavOnClick) => {
    const hasSubitems = item.subitems?.length && itemsAreAllowed(item.subitems);
    if (hasSubitems) {
      setSubnav(item.label);
    } else if (closeNavOnClick) {
      setIsMobileNavOpen(false);
    }
    if (subnav || !hasSubitems) {
      item.onClick();
      if (item.path === 'home') setSubnav(null);
    }
  };

  const handleHomeClick = () => {
    clearFiltersAndNavigate('/dashboard/home');
  }

  const currentHeaderItem = useMemo(() => subnav ? headerItems.find(item => item.label === subnav) : headerItems.find(item => checkPath(item.path)), [headerItems, subnav]);

  const itemsToDisplay = useMemo(
    () => subnav ? currentHeaderItem?.subitems : headerItems, [subnav, currentHeaderItem, headerItems]
  );
  
  const { pathname } = useLocation();  

  const bestMatchIndex = itemsToDisplay?.map((item, idx) =>({
      idx,
      exact: item.path === pathname,
      includes: pathname.includes(item.path),
      length: item.path.length
    }))
    .filter(match => match.exact || match.includes)
    .sort((a, b) => {
      // Prefer exact match, then longest path match
      if (a.exact && !b.exact) return -1;
      if (!a.exact && b.exact) return 1;
      if (a.includes && b.includes) return b.length - a.length;
      if (a.includes) return -1;
      if (b.includes) return 1;
      return 0;
    })[0]?.idx;

  const NavItem = ({item, closeNavOnClick = true, selected, icon}) => <ListItem disablePadding>
    <ListItemButton
      selected={selected}
      sx={{
        '&.Mui-selected': {
          borderRadius: '8px',
          mx: 1
        }
      }}
      onClick={() => handleClick(item, closeNavOnClick)}
    >
      <ListItemText primary={item.label}/>
      {icon && (
        <Iconify icon={icon} sx={{ ml: 1, fontSize: 20, verticalAlign: 'middle' }} />
      )}
    </ListItemButton>
  </ListItem>

  NavItem.propTypes = {
    item: PropTypes.shape({
      path: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      onClick: PropTypes.func
    }),
    closeNavOnClick: PropTypes.bool,
    selected: PropTypes.bool,
    icon: PropTypes.string
  }

  const MainMenu = () => <Box display="flex" alignItems="center" fontWeight={'bold'}>
    <Iconify
      icon="mdi:arrow-left"
      sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }}
    />
    <span>Main Menu</span>
  </Box>
  
  return <Box sx={{ width: 250 }} role="presentation">
    <List>
      <NavItem
        item={{
          label: subnav ? <MainMenu/> : "Home",
          path: subnav ? currentHeaderItem?.path : "home",
          onClick: () => subnav ? setSubnav(null) : handleHomeClick(),
        }}
        closeNavOnClick={false}
        selected={!subnav && checkPath('home')}
        icon={subnav ? null : "mdi:home"}
      />
      {subnav && <Divider sx={{borderWidth: '1px', mb: 1.5, mr: 2, ml: 2}}/>}
      {itemsToDisplay.map((item, idx) => (
        item?.scopes?.length ? <ScopeGuard scopes={item.scopes} allowAdmin={item.allowAdmin} key={item.label}>
          <NavItem
            key={item.label}
            item={item}
            selected={bestMatchIndex === idx}
            icon={item?.subitems?.length && itemsAreAllowed(item?.subitems) ? "mdi:chevron-right" : null}
        />
        </ScopeGuard> : <NavItem
          key={item.label}
          item={item}
          selected={bestMatchIndex === idx}
          icon={item?.subitems?.length && itemsAreAllowed(item?.subitems) ? "mdi:chevron-right" : null}
        />
      ))}
    </List>
  </Box>
}